// BASE_URL will be resolved dynamically below

const getToken = () => {
  const token = localStorage.getItem('study-app-token');
  return token;
};

const resolveBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BASE_URL?.trim();
  if (envUrl) return envUrl;

  if (typeof window === 'undefined') {
    throw new Error('API base URL is not configured for non-browser environment.');
  }

  // For local development, CRA proxy handles '/api/v1'
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return '/api/v1';
  }

  // For production, use nginx proxy paths
  return window.location.pathname.startsWith('/smartlms')
    ? '/smartlms-api'
    : '/api/v1';
};


export const BASE_URL = resolveBaseUrl();

const handleResponse = async (response) => {
  console.log(' Response status:', response.status);
  
  if (!response.ok) {
    let errorData;
    let errorText;
    
    try {
      errorText = await response.text();
      console.log('Raw error response text:', errorText);
      
      if (errorText) {
        errorData = JSON.parse(errorText);
      } else {
        errorData = { message: `HTTP error! status: ${response.status} ${response.statusText}` };
      }
    } catch (e) {
      console.error('Error parsing error response:', e);
      errorData = { 
        message: `HTTP error! status: ${response.status} ${response.statusText}`,
        rawText: errorText 
      };
    }

    let errorMessage;
    if (errorData.detail) {
      errorMessage = errorData.detail;
    } else if (errorData.message) {
      errorMessage = errorData.message;
    } else if (errorData.msg) {
      errorMessage = errorData.msg;
    } else {
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }

    // Authentication errors handling
    if (response.status === 401 || response.status === 403) {
      // If this error originated from an auth endpoint (login/register/refresh),
      // surface the backend-provided message (e.g. "Invalid credentials") so the
      // login form can show the specific error. For other endpoints, treat as
      // session expiration and clear local auth state.
      const requestUrl = response.url || '';
      const isAuthEndpoint = /\/auth\/(login|register|refresh)/.test(requestUrl);

      if (isAuthEndpoint) {
        throw new Error(errorMessage);
      }

      // Normalize common backend message into a user-friendly string for other endpoints
      const friendly = 'Your session has expired or you do not have access. Please sign in again.';

      // Remove local auth state so UI reacts to missing token
      try {
        localStorage.removeItem('study-app-token');
        localStorage.removeItem('study-app-user');
      } catch (e) {
        // ignore storage errors
      }

      // Broadcast an event so any React context or components can listen and react (optional)
      try {
        if (typeof window !== 'undefined' && window.document && typeof window.CustomEvent === 'function') {
          const evt = new CustomEvent('sessionExpired', { detail: { status: response.status, raw: errorMessage } });
          window.dispatchEvent(evt);
        }
      } catch (e) {
        // ignore event dispatch errors
      }

      throw new Error(friendly);
    }

    throw new Error(errorMessage);
  }
  
  try {
    const text = await response.text();
    
    if (!text) {
      return {};
    }
    
    return JSON.parse(text);
  } catch (e) {
    console.error('Error parsing success response:', e);
    throw new Error('Invalid JSON response from server');
  }
};




export const startSession = async () => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const urlParams = new URLSearchParams({
      access_token: token
    });

    let url = `${BASE_URL}/start-session?${urlParams.toString()}`;

    console.log('Starting new session with GET:', url);
    
    const response = await fetch(url, {
      method: 'GET', 
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await handleResponse(response);
    console.log('Session started:', result);
    return result;

  } catch (error) {
    console.error(' Error starting session:', error);
    throw error;
  }
};

export const startMockTest = async () => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const urlParams = new URLSearchParams({
      access_token: token
    });

    let url = `${BASE_URL}/start-mock-test?${urlParams.toString()}`;

    console.log('Starting mock test session:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await handleResponse(response);
    console.log('Mock test session started:', result);
    return result;

  } catch (error) {
    console.error('Error starting mock test session:', error);
    
    console.log('Mock test endpoint not available, using regular session as fallback');
    return await startSession();
  }
};

export const getNextMockQuestion = async (sessionId) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const urlParams = new URLSearchParams({
      access_token: token
    });
    
    if (sessionId) {
      urlParams.append('session_id', sessionId);
    }

    let url = `${BASE_URL}/next-mock-test?${urlParams.toString()}`;

    console.log('Fetching next mock test question:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching next mock question:', error);
    throw error;
  }
};

export const submitAnswer = async (questionId, userAnswer, confidenceLevel, timeSpent, sessionId = null) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const questionIdNum = typeof questionId === 'string' ? parseInt(questionId) : questionId;

    const urlParams = new URLSearchParams({
      access_token: token
    });
    
    let url = `${BASE_URL}/submit-answer?${urlParams.toString()}`;
    
    const payload = {
      question_id: questionIdNum,
      user_answer: userAnswer,
      confidence_level: confidenceLevel,
      session_id: sessionId || `session_${Date.now()}`
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const result = await handleResponse(response);
    return result;

  } catch (error) {
    console.error('Error submitting answer:', error);
    throw error;
  }
};

export const getUserStats = async (sessionId = null) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const urlParams = new URLSearchParams({
      access_token: token
    });
    
    if (sessionId) {
      urlParams.append('session_id', sessionId);
    }

    let url = `${BASE_URL}/user-stats?${urlParams.toString()}`;

    console.log('Fetching user stats:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
};

export const getNextQuestion = async (topic, difficulty = 'medium', sessionId = null) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const params = new URLSearchParams({
      topic: topic,
      difficulty: difficulty,
      access_token: token
    });
    
    if (sessionId) {
      params.append('session_id', sessionId);
    }

    const url = `${BASE_URL}/next?${params.toString()}`;

    console.log('Fetching next question from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching next question:', error);
    throw error;
  }
};

export const getQuestions = async (topic = null) => {
  try {
    
    const token = getToken();
    
    if (!token) {
      console.log('No token, returning empty questions array');
      return [];
    }

    const urlParams = new URLSearchParams({
      access_token: token
    });
    
    if (topic) {
      urlParams.append('topic', topic);
    }

    let url = `${BASE_URL}/questions?${urlParams.toString()}`;

    console.log('Fetching questions from API:', url);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching questions, returning empty array:', error);
    return [];
  }
};

export const getTopics = async () => {
  try {
    const token = getToken();
    
    if (!token) {
      console.log('No token available, returning empty topics array');
      return [];
    }
    
    const urlParams = new URLSearchParams({
      access_token: token
    });

    let url = `${BASE_URL}/topics?${urlParams.toString()}`;

    console.log('Fetching topics from API:', url);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    const data = await handleResponse(response);
    
    if (data && data.topics && Array.isArray(data.topics)) {
      const transformedTopics = data.topics.map((topicObj, index) => ({
        id: topicObj.topic, 
        name: formatTopicName(topicObj.topic),
        api_topic: topicObj.topic,
        questions: topicObj.total_questions || 0
      }));
      
      console.log('Transformed topics:', transformedTopics);
      return transformedTopics;
    }
    
    console.log('No topics found from API, returning empty array');
    return [];
    
  } catch (error) {
    console.error('Error fetching topics, returning empty array:', error);
    return [];
  }
};

export const getProgress = async (topic = 'all') => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const urlParams = new URLSearchParams({
      access_token: token
    });
    
    if (topic && topic !== 'all') {
      urlParams.append('topic', topic);
    }

    let url = `${BASE_URL}/progress?${urlParams.toString()}`;

    console.log('Fetching progress data:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching progress:', error);
    throw error;
  }
};

export const login = async (identifier, password) => {
  try {
  // FastAPI typically treats '/auth/login' and '/auth/login/' differently; using the canonical path without trailing slash
  const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        identifier: identifier,
        password: password
      }),
    });

    const data = await handleResponse(response);
    return data;
  } catch (error) {
    console.error('Error during login:', error);
    throw error;
  }
};

export const register = async (email, username, password) => {
  try {
  const response = await fetch(`${BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        username: username,
        password: password
      }),
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Error during registration:', error);
    throw error;
  }
};

export const getUserAnswers = async (sessionId = null) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const urlParams = new URLSearchParams({
      access_token: token
    });
    
    if (sessionId) {
      urlParams.append('session_id', sessionId);
    }

    let url = `${BASE_URL}/user-answers?${urlParams.toString()}`;

    console.log('Fetching user answers:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching user answers:', error);
    throw error;
  }
};

export const formatTopicName = (topic) => {
  if (!topic) return 'Unknown Topic';
  
  const topicMap = {
    'general_science': 'General Science',
    'world_history_civilizations': 'World History & Civilizations',
    'personal_finance_investing': 'Personal Finance & Investing'
  };

  return topicMap[topic] || topic
    .split('_')
    .map(word => {
      if (word.match(/^[a-z]\d+$/i)) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

// Admin API endpoints
export const getCurrentUser = async () => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    let url = `${BASE_URL}/auth/me`;

    console.log('Fetching current user from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

export const getAdminUserStats = async () => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    let url = `${BASE_URL}/admin/users/stats`;

    console.log('Fetching user stats from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
};

export const getAdminUsers = async (params = {}) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const urlParams = new URLSearchParams();
    if (params.limit) urlParams.append('limit', params.limit);
    if (params.offset !== undefined) urlParams.append('offset', params.offset);
    if (params.search) urlParams.append('search', params.search);
    if (params.is_active !== undefined) urlParams.append('is_active', params.is_active);
    if (params.sort_by) urlParams.append('sort_by', params.sort_by);
    if (params.sort_order) urlParams.append('sort_order', params.sort_order);
    if (params.include_deleted) urlParams.append('include_deleted', params.include_deleted);

    let url = `${BASE_URL}/admin/users`;
    if (urlParams.toString()) {
      url += `?${urlParams.toString()}`;
    }

    console.log('Fetching admin users from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching admin users:', error);
    throw error;
  }
};

export const getAdminUserById = async (userId) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    let url = `${BASE_URL}/admin/users/${userId}`;

    console.log('Fetching admin user by ID from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching admin user by ID:', error);
    throw error;
  }
};

export const createAdminUser = async (userData) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    let url = `${BASE_URL}/admin/users`;

    console.log('Creating admin user:', url);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(userData)
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error creating admin user:', error);
    throw error;
  }
};

export const updateAdminUser = async (userId, updateData) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    let url = `${BASE_URL}/admin/users/${userId}`;

    console.log('Updating admin user:', url, updateData);
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData)
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error updating admin user:', error);
    throw error;
  }
};

export const deleteAdminUser = async (userId, hardDelete = false) => {
  try {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const urlParams = new URLSearchParams();
    if (hardDelete) urlParams.append('hard_delete', 'true');

    let url = `${BASE_URL}/admin/users/${userId}`;
    if (urlParams.toString()) {
      url += `?${urlParams.toString()}`;
    }

    console.log('Deleting admin user:', url);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error deleting admin user:', error);
    throw error;
  }
};

export const apiService = {
  register,
  login,
  getQuestions,
  submitAnswer,
  getTopics,
  getNextQuestion,
  getUserAnswers,
  formatTopicName,
  startSession,
  getUserStats,
  getProgress,
  startMockTest,
  getNextMockQuestion,
  // Admin endpoints
  getCurrentUser,
  getAdminUsers,
  getAdminUserById,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  getAdminUserStats
};
